import {ApiConfig} from '../config/ApiConfig';
import {getAccessToken} from '../helpers/storage';

/**
 * Self-service user account API for the settings screens.
 *
 * Mirrors the auth/error conventions of notificationApi.js:
 *   - Bearer token from secure storage on every call.
 *   - JSON responses parsed defensively (204 / empty body tolerated).
 *
 * Endpoints (backend: HappyPOS-backend src/api/controllers/user.controller.js):
 *   - PUT  /api/user                       update own profile (name, phone, …)
 *   - POST /api/user/me/change-password    change password (currentPassword + newPassword)
 *   - POST /api/user/me/avatar             multipart avatar upload → user.photo
 */

const USER_URL = `${ApiConfig.apiURL}/api/user`;
const CHANGE_PASSWORD_URL = `${ApiConfig.apiURL}/api/user/me/change-password`;
const PASSWORD_CHECK_URL = `${ApiConfig.apiURL}/api/auth/password/check`;
const AVATAR_URL = `${ApiConfig.apiURL}/api/user/me/avatar`;
const DICEBEAR_AVATAR_URL = `${ApiConfig.apiURL}/api/user/me/avatar/dicebear`;
const AI_SUGGEST_AVATAR_URL = `${ApiConfig.apiURL}/api/user/me/avatar/ai-suggest`;
const AI_IMAGE_AVATAR_URL = `${ApiConfig.apiURL}/api/user/me/avatar/ai-image`;
const DASHBOARD_CONFIG_URL = `${ApiConfig.apiURL}/api/user/me/dashboard-config`;

/**
 * Curated DiceBear avatar styles offered in the "choose an avatar" picker. Must
 * match the backend allowlist (user.controller.js DICEBEAR_STYLES).
 * @type {string[]}
 */
export const DICEBEAR_STYLES = ['fun-emoji', 'big-smile', 'adventurer', 'lorelei', 'bottts', 'thumbs', 'micah', 'notionists'];

const buildErrorFromResponse = async (response) => {
    try {
        const body = await response.json();
        const message = body?.message || body?.error || response.statusText;
        const error = new Error(message || `Request failed with status ${response.status}`);
        // Surface the server's error code (e.g. 422 PASSWORD_PWNED) so callers can
        // branch on it without re-parsing the response body.
        if (body?.code) error.code = body.code;
        if (typeof body?.breachCount === 'number') error.breachCount = body.breachCount;
        return error;
    } catch (_error) {
        return new Error(response.statusText || `Request failed with status ${response.status}`);
    }
};

const parseApiResponse = async (response) => {
    if (!response.ok) {
        throw await buildErrorFromResponse(response);
    }
    if (response.status === 204) {
        return true;
    }
    const text = await response.text();
    if (!text) {
        return true;
    }
    try {
        return JSON.parse(text);
    } catch (_error) {
        return text;
    }
};

const authHeaders = async () => {
    const accessToken = await getAccessToken();
    if (!accessToken) {
        const authError = new Error('Authentication required');
        authError.code = 'AUTH_REQUIRED';
        throw authError;
    }
    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
    };
};

/**
 * Update the authenticated user's own profile fields.
 * Scoped server-side to req.user._id (legacy `PUT /api/user` update handler).
 *
 * @param {{name?: {first?: string, last?: string}, phone?: string, company?: string, language?: string}} patch
 * @returns {Promise<object>} the serialized updated user
 */
export async function updateMyProfile(patch = {}) {
    const response = await fetch(USER_URL, {
        method: 'PUT',
        headers: await authHeaders(),
        body: JSON.stringify(patch),
    });
    return parseApiResponse(response);
}

/**
 * Step-up–gated self-service settings write (mirrors the admin settings page).
 * `PUT /api/user/me/secure-settings` rejects with 401 STEP_UP_REQUIRED unless a
 * valid step-up token (see services/stepUpApi.js) proves the user just
 * re-authenticated with their password / 2FA / passkey.
 *
 * @param {{profile?: object, language?: string, notificationPreferences?: object, billing?: object}} sections
 * @param {string} stepUpToken token minted by POST /api/auth/step-up/verify
 * @returns {Promise<{user?: object, restaurant?: object}>}
 */
export async function updateMySecureSettings(sections = {}, stepUpToken) {
    const response = await fetch(`${ApiConfig.apiURL}/api/user/me/secure-settings`, {
        method: 'PUT',
        headers: {
            ...(await authHeaders()),
            ...(stepUpToken ? {'X-Step-Up-Token': stepUpToken} : {}),
        },
        body: JSON.stringify(sections),
    });
    return parseApiResponse(response);
}

/**
 * Change the authenticated user's password. The current password proves
 * ownership, so no step-up token is needed.
 *
 * @param {string} currentPassword
 * @param {string} newPassword
 * @returns {Promise<{success: boolean}>}
 */
export async function changeMyPassword(currentPassword, newPassword) {
    const response = await fetch(CHANGE_PASSWORD_URL, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({currentPassword, newPassword}),
    });
    return parseApiResponse(response);
}

/**
 * Check whether a candidate password appears in the Have I Been Pwned breach
 * corpus (`POST /api/auth/password/check`, public — works pre-login). Used to
 * warn the user early on the change-password screen; the server is the real
 * gate and still rejects breached passwords on submit with a 422
 * `PASSWORD_PWNED` error.
 *
 * `checked: false` means the check was skipped or HIBP was unreachable
 * (fail-open) — callers must NOT block the user in that case.
 *
 * @param {string} password candidate password to check
 * @returns {Promise<{pwned: boolean, blocked: boolean, breachCount: number, checked: boolean, code: string|null, message: string|null}>}
 */
export async function checkPasswordPwned(password) {
    const response = await fetch(PASSWORD_CHECK_URL, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({password}),
    });
    return parseApiResponse(response);
}

/**
 * Upload a new avatar image (multipart/form-data). The backend stores it on
 * Cloudflare R2 and sets user.photo to the resulting public URL.
 *
 * @param {{uri: string, name?: string, type?: string}} asset image asset from expo-image-picker
 * @returns {Promise<{photo: string, user: object}>}
 */
export async function uploadMyAvatar(asset) {
    const accessToken = await getAccessToken();
    if (!accessToken) {
        const authError = new Error('Authentication required');
        authError.code = 'AUTH_REQUIRED';
        throw authError;
    }

    const uri = asset?.uri;
    if (!uri) {
        throw new Error('No image selected');
    }

    const guessedName = asset?.name || uri.split('/').pop() || `avatar-${Date.now()}.jpg`;
    const guessedType = asset?.type && asset.type.startsWith('image/')
        ? asset.type
        : (/\.png$/i.test(guessedName) ? 'image/png' : 'image/jpeg');

    const formData = new FormData();
    // RN's fetch builds the multipart boundary itself — do NOT set Content-Type.
    formData.append('file', {uri, name: guessedName, type: guessedType});

    const response = await fetch(AVATAR_URL, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
        },
        body: formData,
    });
    return parseApiResponse(response);
}

/**
 * Set the avatar from a curated DiceBear collection. The backend renders the
 * chosen style to a PNG and self-hosts it on R2, returning the new user.photo.
 *
 * @param {string} style one of {@link DICEBEAR_STYLES}
 * @param {string} [seed] deterministic look seed (defaults server-side to the user id)
 * @returns {Promise<{photo: string, user: object}>}
 */
export async function setMyDicebearAvatar(style, seed) {
    const response = await fetch(DICEBEAR_AVATAR_URL, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(seed ? {style, seed} : {style}),
    });
    return parseApiResponse(response);
}

/**
 * Ask the AI to suggest a DiceBear avatar from a free-text description. The
 * backend maps the prompt to one of the allowlisted {@link DICEBEAR_STYLES}
 * plus a deterministic seed. This is a pure suggestion: it does NOT change the
 * stored avatar — the caller previews it (e.g. via the picker's preview grid)
 * and confirms by calling {@link setMyDicebearAvatar} with the same values.
 *
 * @param {string} prompt natural-language description of the desired avatar
 * @returns {Promise<{style: string, seed: string}>} suggested DiceBear style + seed
 */
export async function suggestAiAvatar(prompt) {
    const response = await fetch(AI_SUGGEST_AVATAR_URL, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({prompt}),
    });
    return parseApiResponse(response);
}

/**
 * Generate a unique, AI-illustrated avatar image from a free-text description.
 * Unlike {@link suggestAiAvatar}, this DOES persist the result: the backend
 * renders the image, stores it on Cloudflare R2 and sets it as the user's
 * photo, returning the new public URL and the updated user document.
 *
 * This consumes AI credits, so the backend may reject the call with HTTP 403
 * when the account is out of credits — {@link parseApiResponse} surfaces that
 * as a thrown Error the caller should handle gracefully (e.g. a toast).
 *
 * @param {string} prompt natural-language description of the desired avatar
 * @param {string} [restaurantId] active restaurant id; sent only when provided.
 *   When omitted, the backend falls back to the user's own restaurant.
 * @returns {Promise<{photo: string, user: object}>} new photo URL + updated user
 */
export async function generateAiAvatarImage(prompt, restaurantId) {
    const body = restaurantId ? {prompt, restaurantId} : {prompt};
    const response = await fetch(AI_IMAGE_AVATAR_URL, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(body),
    });
    return parseApiResponse(response);
}

/**
 * Fetch the authenticated user's notification gate context from `GET /api/user/me`.
 *
 * Returns the full user document, including `roles`, `dynamicRoles`,
 * `notificationPreferences` and `restaurants[]`. The notification settings panel
 * uses this to determine which event categories are visible for the user's role
 * and to seed the preference form with the server's current values.
 *
 * @returns {Promise<{user: object, notificationPreferences: object|null}>}
 */
export async function getMyNotificationContext() {
    const response = await fetch(`${ApiConfig.apiURL}/api/user/me`, {
        method: 'GET',
        headers: await authHeaders(),
    });
    const user = await parseApiResponse(response);
    return {
        user,
        notificationPreferences: user?.notificationPreferences ?? null,
    };
}

/**
 * Read one dashboard surface's saved layout from the account
 * (`GET /api/user/me/dashboard-config?namespace=…`).
 *
 * A dedicated, ~1 KB endpoint on purpose: `/api/user/me` carries every
 * populated restaurant and runs into the megabytes on a multi-store account,
 * which is far too heavy to pull on dashboard mount just to learn which four
 * KPI tiles the user pinned.
 *
 * @param {string} namespace dashboard surface (e.g. `'mobileManagement'`)
 * @returns {Promise<object>} the stored layout entry, `{}` when never customized
 */
export async function getMyDashboardConfig(namespace) {
    const response = await fetch(`${DASHBOARD_CONFIG_URL}?namespace=${encodeURIComponent(namespace)}`, {
        method: 'GET',
        headers: await authHeaders(),
    });
    const body = await parseApiResponse(response);

    return (body && typeof body.config === 'object' && body.config) || {};
}

/**
 * Persist one dashboard surface's layout on the account
 * (`PUT /api/user/me/dashboard-config`).
 *
 * The backend merges PER NAMESPACE, so this write can never wipe the layouts
 * the admin web saved for the same user. Pass `null` to reset the surface back
 * to the client's defaults.
 *
 * @param {string} namespace dashboard surface (e.g. `'mobileManagement'`)
 * @param {object|null} config the layout entry to store, or `null` to reset
 * @returns {Promise<object>} the full `dashboardConfig` blob after the merge
 */
export async function saveMyDashboardConfig(namespace, config) {
    const response = await fetch(DASHBOARD_CONFIG_URL, {
        method: 'PUT',
        headers: await authHeaders(),
        body: JSON.stringify({namespace, config}),
    });
    const body = await parseApiResponse(response);

    return (body && typeof body.dashboardConfig === 'object' && body.dashboardConfig) || {};
}

/**
 * Persist SEVERAL dashboard namespaces in one request
 * (`PUT /api/user/me/dashboard-config` with the `{dashboardConfig: {...}}` body).
 *
 * Same per-namespace merge as {@link saveMyDashboardConfig}: only the namespaces
 * present in `patch` are replaced (wholesale), a `null` value deletes one, and
 * everything else on the account (admin boards, the legacy manager layout) is
 * preserved.
 *
 * @param {Object.<string, ?object>} patch namespace -> entry (or `null` to reset)
 * @returns {Promise<object>} the full `dashboardConfig` blob after the merge
 */
export async function saveMyDashboardConfigPatch(patch) {
    const response = await fetch(DASHBOARD_CONFIG_URL, {
        method: 'PUT',
        headers: await authHeaders(),
        body: JSON.stringify({dashboardConfig: patch}),
    });
    const body = await parseApiResponse(response);

    return (body && typeof body.dashboardConfig === 'object' && body.dashboardConfig) || {};
}
